import { motion } from 'framer-motion'
import { Users, TrendingDown, Briefcase, Sparkles, Target, CreditCard } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const WhoWeHelp = () => {
  const { t } = useLanguage()
  
  const targetGroups = [
    {
      icon: TrendingDown,
      text: t('about.whoWeHelp.item1'),
      color: 'from-red-500 to-orange-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      iconColor: 'text-red-400'
    },
    {
      icon: Briefcase,
      text: t('about.whoWeHelp.item2'),
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      iconColor: 'text-blue-400'
    },
    {
      icon: Users,
      text: t('about.whoWeHelp.item3'),
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      iconColor: 'text-purple-400'
    },
    {
      icon: Sparkles,
      text: t('about.whoWeHelp.item4'),
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      iconColor: 'text-yellow-400'
    },
    {
      icon: Target,
      text: t('about.whoWeHelp.item5'),
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      iconColor: 'text-green-400'
    },
    {
      icon: CreditCard,
      text: t('about.whoWeHelp.item6'),
      color: 'from-indigo-500 to-blue-500',
      bgColor: 'bg-indigo-500/10',
      borderColor: 'border-indigo-500/30',
      iconColor: 'text-indigo-400'
    }
  ]

  return (
    <section className="py-12 lg:py-16 bg-gradient-to-b from-black via-gray-900/50 to-black relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
            <Users className="h-4 w-4 text-blue-400" />
            <span className="text-gray-300 text-sm">{t('about.mission.badge')}</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            {t('about.whoWeHelp.titlePrefix')} <span className="text-gradient">{t('about.whoWeHelp.titleHighlight')}</span>
          </h2>
          
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            {t('about.whoWeHelp.subtitle')}
          </p>
        </motion.div>

        {/* Grid of target groups */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {targetGroups.map((group, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`group relative p-6 rounded-2xl border ${group.borderColor} ${group.bgColor} backdrop-blur-sm hover:scale-105 transition-all duration-300 hover:shadow-2xl`}
            >
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl ${group.bgColor} border ${group.borderColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <group.icon className={`h-7 w-7 ${group.iconColor}`} />
              </div>
              
              {/* Text */}
              <p className="text-gray-200 font-medium leading-relaxed">
                {group.text}
              </p>

              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${group.color} opacity-0 group-hover:opacity-5 transition-opacity pointer-events-none`} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default WhoWeHelp
